/**
 * Maturity parser for trade booking
 * Parses strings like "1Y", "3M2D", "1Y3M2D" into total calendar days
 */

export interface MaturityParseResult {
  success: boolean;
  days: number;
  error?: string;
}

/**
 * Parse maturity string to total number of calendar days
 * Supports formats like: "1Y", "3M", "2D", "1Y3M", "3M2D", "1Y3M2D", "365", etc.
 *
 * Conversion rates (calendar days):
 * - Y (years) = 365 days
 * - M (months) = 30 days
 * - D (days) = 1 day
 *
 * @param input - Maturity string (e.g., "1Y3M2D")
 * @returns Parse result with total days or error
 */
export const parseMaturity = (input: string): MaturityParseResult => {
  if (!input || input.trim() === '') {
    return { success: false, days: 0, error: 'Maturity is required' };
  }

  const trimmed = input.trim().toUpperCase();

  // If it's just a number, treat it as days
  if (/^\d+$/.test(trimmed)) {
    const days = parseInt(trimmed, 10);
    return { success: true, days };
  }

  // Parse Y, M, D components
  // Regex: optional digits followed by Y, M, or D
  const regex = /(\d+)([YMD])/g;
  const matches = Array.from(trimmed.matchAll(regex));

  if (matches.length === 0) {
    return {
      success: false,
      days: 0,
      error: 'Invalid maturity format. Use Y (years), M (months), D (days), e.g., 1Y3M2D',
    };
  }

  let totalDays = 0;
  const seen = new Set<string>();

  for (const match of matches) {
    const value = parseInt(match[1], 10);
    const unit = match[2];

    // Check for duplicate units
    if (seen.has(unit)) {
      return {
        success: false,
        days: 0,
        error: `Duplicate unit "${unit}" in maturity string`,
      };
    }
    seen.add(unit);

    // Convert to days
    switch (unit) {
      case 'Y':
        totalDays += value * 365;
        break;
      case 'M':
        totalDays += value * 30;
        break;
      case 'D':
        totalDays += value;
        break;
    }
  }

  // Validate result
  if (totalDays <= 0) {
    return {
      success: false,
      days: 0,
      error: 'Maturity must be greater than 0',
    };
  }

  // Check if there are any unmatched characters
  const reconstructed = matches.map(m => m[0]).join('');
  if (reconstructed !== trimmed) {
    return {
      success: false,
      days: 0,
      error: 'Invalid characters in maturity string',
    };
  }

  return { success: true, days: totalDays };
};

/**
 * Validate maturity string format
 * @param input - Maturity string
 * @returns True if valid, false otherwise
 */
export const isValidMaturity = (input: string): boolean => {
  const result = parseMaturity(input);
  return result.success;
};

/**
 * Format days back to maturity string (e.g., 395 days -> "1Y1M")
 * Uses approximations: 365 days = 1Y, 30 days = 1M
 * @param days - Total number of days
 * @returns Formatted maturity string
 */
export const formatMaturity = (days: number): string => {
  if (days === 0) return '0D';

  let remaining = days;
  const years = Math.floor(remaining / 365);
  remaining = remaining % 365;

  const months = Math.floor(remaining / 30);
  remaining = remaining % 30;

  const resultDays = remaining;

  const parts: string[] = [];
  if (years > 0) parts.push(`${years}Y`);
  if (months > 0) parts.push(`${months}M`);
  if (resultDays > 0) parts.push(`${resultDays}D`);

  return parts.join('') || '0D';
};
